import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppConfigProvider } from '@app/app.config.provider';
import { environment } from '@environments/environment';
import { EmpyreanApplicationName, isUserLevelForApplication, IUserLevel } from '@models/user-level';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { GlobalSettingsService } from '@services/global/global-settings.service';
import { isEmpty } from '@shared/arrays';
import { User, UserManager } from 'oidc-client';
import { ReplaySubject } from 'rxjs';
import Swal from 'sweetalert2';

const TIMEOUT_TIMER = 30; // 30s till timeout
const DEFAULT_IDLE_TIMER = 5 * 60;

// For debugging purposes
// const DEFAULT_IDLE_TIMER = 10;
// const TIMEOUT_TIMER = 10;

@Injectable({ providedIn: 'root' })
export class OpenIDConnectService {
  private userManager: UserManager;
  private currentUser: User;
  private countdownTimer: number;
  private popup;
  private maxIdleTime = DEFAULT_IDLE_TIMER;

  userLoaded$ = new ReplaySubject<boolean>(1);

  get userAvailable(): boolean {
    return !!this.currentUser;
  }

  get user(): User {
    return this.currentUser;
  }

  get userId(): number {
    if (this.userAvailable) {
      return Number(this.currentUser.profile.sub);
    }
    // else {
    //   this.triggerSignIn();
    // }
  }

  get isIdling(): boolean {
    return this.idle ? this.idle.isIdling() : false;
  }

  get customerId(): number {
    const customerId = Number(this.currentUser.profile.Custaim);
    if (!isNaN(customerId) && typeof customerId === 'number') {
      return customerId as number;
    } else {
      console.warn(`customerId not found for current user: ${customerId}`);
      return null;
    }
  }

  get userLevelList(): IUserLevel[] {
    if (this.userAvailable && this.currentUser.profile.userlevels) {
      return JSON.parse(this.currentUser.profile.userlevels);
    } else {
      return [{ level_id: -1, level_text: 'User level not found' }];
    }
  }

  constructor(
    private readonly router: Router,
    private readonly idle: Idle,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly globalSettingsService: GlobalSettingsService,
    private readonly appConfig: AppConfigProvider
  ) {
    this.appConfig.environment.subscribe(env => {
      let oidcSettings = env.openIdConnectSettings;

      let { host } = window.location;
      const allowedHost = env.ALLOWED_URLS.find((url: string) => host.includes(url));

      const fragments = host.split(`${allowedHost}`);

      if (allowedHost && fragments && fragments.length > 0 && !isEmpty(fragments[0])) {
        //
        let idpFragment = fragments[0];
        if (idpFragment.indexOf('.') === idpFragment.length - 1) {
          idpFragment = idpFragment.substring(0, idpFragment.indexOf('.'));
        }
        host = host.replace(/\/\s*$/, ''); // removes backlash at the end, if exists
        oidcSettings = {
          ...env.openIdConnectSettings,
          acr_values: `idp:${idpFragment}`,
          silent_redirect_uri: `https://${host}/redirect-silentrenew`,
          redirect_uri: `https://${host}/signin-oidc`,
          post_logout_redirect_uri: `https://${host}`
        };
      }

      this.userManager = new UserManager(oidcSettings);
      // ***********
      // OIDC Events
      // ***********
      this.userManager.clearStaleState();

      this.userManager.events.addUserLoaded(user => {
        if (!environment.production) {
          // console.log('User loaded', user, this.currentUser);
        }

        try {
          if (this.currentUser && user && this.currentUser.profile.sub === user.profile.sub && this.currentUser.scope === user.scope) {
            this.currentUser = user;
            // console.log('TCL: OpenIDConnectService -> this.currentUser = user', user);
          } else {
            if (this.getDepositsUserLevelFromProfile(user.profile)) {
              this.loadIncomingUser(user);
            } else {
              // console.error('OpenIdConnectService -> No deposits user found in token');
              this.document.location.href = 'error/401';
            }
          }
        } catch (err) {
          // console.log('TCL: OpenIDConnectService -> err', err);
          this.loadIncomingUser(user);
        }

        this.idle.watch();
        this.userManager.startSilentRenew();
      });

      this.userManager.events.addUserUnloaded(() => {
        if (!environment.production) {
          console.log('User unloaded');
        }

        // TODO: Permissions service
        // this.permissionService.clearTabsFromSessionStorage();
        this.currentUser = null;
        this.userLoaded$.next(false);
      });
    });

    // ***********
    // Idle Config
    // ***********

    // sets an idle timeout of maxIdleTime seconds
    this.idle.setIdle(this.maxIdleTime);
    // sets a timeout period of TIMEOUT_TIMER seconds.
    // We are not, however, using the timeout implementation of the library.
    // After the TimeoutStarts event happen, we force the user to click the notification otherwise they will time out
    this.idle.setTimeout(TIMEOUT_TIMER);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // ***********
    // Idle Events
    // ***********
    this.idle.onIdleEnd.subscribe(() => {
      // console.log('No longer idle.');
    });

    this.idle.onTimeout.subscribe(() => {
      // this.triggerSignOut();
      this.logout();
    });

    this.idle.onIdleStart.subscribe(() => {
      let timerInterval;
      if (!this.popup && this.userAvailable) {
        this.popup = Swal.fire({
          title: `You're about to be signed out`,
          html:
            '<p>For security reasons, your connection will time out after you’ve been inactive for a while. ' +
            ' Click the button to stay signed in. <span id="timeoutAmt"></span></p>',
          timer: (TIMEOUT_TIMER + 1) * 1000,
          confirmButtonText: 'Stay Signed In',
          allowOutsideClick: false,
          // This creates the interval that will handle the countdown on the SWAL element
          onBeforeOpen: () => {
            const content = Swal.getContent();
            this.countdownTimer = TIMEOUT_TIMER * 10;

            timerInterval = setInterval(() => {
              this.countdownTimer--;

              content.querySelector('#timeoutAmt').textContent =
                this.countdownTimer > 0 ? `Remaining time: ${Math.floor(this.countdownTimer / 10)} s.` : '';
              if (this.countdownTimer <= 0) {
                clearInterval(timerInterval);
                this.logout();
              }
            }, 100);
          },
          onClose: () => {
            this.countdownTimer = TIMEOUT_TIMER;
            this.popup = null;
            clearInterval(timerInterval);
          }
        });
      }
    });
  }

  private getDepositsUserLevelFromProfile(profile) {
    return isUserLevelForApplication(EmpyreanApplicationName.Deposits, JSON.parse(profile.userlevels));
  }

  private loadIncomingUser(user: User) {
    // console.log('TCL: OpenIDConnectService -> loadIncomingUser -> user', user);
    this.currentUser = user;
    // Obtain global params from database and update the max idle time if it's not the default
    this.globalSettingsService
      .getSettings()
      .toPromise()
      .then(settings => {
        const idleSetting = settings.find(set => set.SettingName === 'MAX_IDLE_TIME');
        if (idleSetting) {
          this.maxIdleTime = +idleSetting.SettingValue * 60;
          // this.maxIdleTime = 30;

          // Stops the Idle timer if it's running, update it with the new value, then start again if User is logged in
          if (this.idle.isRunning()) {
            this.idle.stop();
          }
          // Sets the new time
          this.idle.setIdle(this.maxIdleTime);
          if (this.userAvailable) {
            this.idle.watch();
          }
        }
      })
      .catch(err => console.warn(err));
    this.userLoaded$.next(true);
  }

  /**
   * Triggers the OIDC routine to send an user to the OIDC endpoint to retrieve the token
   */
  triggerSignIn() {
    this.userManager
      .signinRedirect()
      .then(data => {
        // if (!environment.production) {
        //   console.log('Redirection to signin triggered.');
        // }
      })
      .catch(err => {
        console.error('Unhandled error when triggering login in', err);
        this.router.navigate(['/error']);
      });
  }

  /**
   * Triggers the OIDC routine to store the token in the session store and in memory
   */
  handleCallBack() {
    this.userManager
      .signinRedirectCallback()
      .then(user => {
        this.currentUser = user;
        // if (!environment.production) {
        //   console.log('Callback after signin triggered.', user);
        // }
      })
      .catch(err => {
        console.warn(`💀 Error when receiving token from identity server`, { err });
        if (err.toString().includes('Error: No matching state found in storage')) {
          this.triggerSignIn();
        }
      });
  }

  handleSilentCallback() {
    this.userManager
      .signinSilentCallback()
      .then(user => {
        // console.log('TCL: OpenIDConnectService -> handleSilentCallback -> user', user);
        // this.currentUser = user;
      })
      .catch(err => {
        console.error('Unhandled error when silently triggering login in', err);
        this.router.navigate(['/error']);
      });
  }

  /**
   * Triggers the SingOut process for the OIDC library
   */
  triggerSignOut() {
    this.userManager
      .signoutRedirect()
      .then(res => {
        // if (!environment.production) {
        //   console.log('Redirection to signout triggered.', res);
        // }
      })
      .catch(err => console.error('Unhandled error when signing out', err))
      .finally(() => this.removeUserFromStorage());
  }

  removeUserFromStorage() {
    this.userManager.removeUser();
    this.userManager.revokeAccessToken();
  }

  /**
   * Routine that will be executed after Idle times the user out
   * It removes the popup if still active, then creates a call to the OIDC endpoint to remove the user
   * from the Identity Server and the frontend.
   * Then, it will stop all tracking services and navigate the user to the Timeout page
   */
  private logout() {
    if (this.popup) {
      this.popup.close();
    }

    try {
      // this.permissionService.clearTabsFromSessionStorage();
      this.userManager
        .createSignoutRequest({ id_token_hint: this.user.id_token, extraQueryParams: { reason: 'timeout' } })
        .then(signoutRequest => {
          this.document.location.href = signoutRequest.url;
        });
    } catch (err) {
      this.triggerSignOut();
    } finally {
      // This code should never execute. The try/catch should redirect to identity server in any case
      this.document.location.href = 'error/timeout'; // Bypasses angular router
      this.idle.stop();
      this.userManager.removeUser();
    }
  }
}
